<template>
  <CountDown v-if="SETTINGS.showCountdown" />

  <v-app v-else class="app" :theme="darkMode ? 'dark' : 'light'">
    <AppHeader />

    <v-main class="py-0 px-2 px-sm-3 px-md-4 app-main">
      <LatestTransactionsCarousel />

      <div
        v-if="smAndDown"
        class="d-flex align-center mt-n2 mb-2 flex-wrap justify-space-between"
      >
        <div class="d-flex align-center">
          <NetworkSelector dense />
          <CurrencySelector dense />
        </div>
        <div class="d-flex align-center">
          <v-btn
            :to="{ name: ROUTE_ABOUT }"
            variant="text"
            class="bcl-app-header-options px-2 px-sm-4"
          >
            {{ $t("blocks.appHeader.about") }}
          </v-btn>
          <v-btn
            :to="{ name: ROUTE_INVITE }"
            variant="text"
            class="bcl-app-header-options px-2 px-sm-4"
          >
            {{ $t("blocks.appHeader.invite") }}
          </v-btn>
        </div>
      </div>
      <CollectInvitationLinkCard />
      <RouterView />

      <ConfirmDialog />
      <TransactionConfirmDialog />
    </v-main>
  </v-app>
</template>

<script lang="ts" setup>
import { storeToRefs } from "pinia";
import { watch } from "vue";
import { useDisplay } from "vuetify";

import { ROUTE_ABOUT, ROUTE_INVITE, SETTINGS } from "@/utils/constants";
import WebSocketClient from "@/libs/WebSocketClient";
import { useAppStore, useNetworkStore } from "@/stores";
import {
  useAeppSdk,
  useCurrencies,
  useCommunityFactory,
  useWalletConnect,
} from "@/composables";
import { OpenAPI } from "@/api/generated";

const appStore = useAppStore();
const { darkMode } = storeToRefs(useAppStore());
const { initSdk, staticAeSdk, getSdk } = useAeppSdk();
const { activeNetwork } = storeToRefs(useNetworkStore());
const { checkWalletConnection } = useWalletConnect();
const { loadAeternityData, loadCurrencyRates } = useCurrencies();
const { getFactory } = useCommunityFactory();

import AppHeader from "@/components/Header/AppHeader.vue";
import CollectInvitationLinkCard from "@/components/Invitation/CollectInvitationLinkCard.vue";
import CountDown from "@/components/CountDown.vue";
import NetworkSelector from "@/components/Header/NetworkSelector.vue";
import CurrencySelector from "@/components/Header/CurrencySelector.vue";
import ConfirmDialog from "./components/Dialogs/ConfirmDialog.vue";
import TransactionConfirmDialog from "./components/Dialogs/TransactionConfirmDialog.vue";
import LatestTransactionsCarousel from "@/components/LatestTransactionsCarousel.vue";

const { smAndDown } = useDisplay();

watch(
  activeNetwork,
  async (network, prevNetwork) => {
    if (SETTINGS.showCountdown || !network) {
      return;
    }
    appStore.isSdkInitialized = false;
    OpenAPI.BASE = `${network.apiUrl}`;
    try {
      await Promise.all([loadAeternityData(), loadCurrencyRates()]);
    } catch (e) {
      console.error("Error loading data", e);
    }
    try {
      await initSdk();
      const sdk = await getSdk();
      sdk.selectNode(network.name);
      staticAeSdk.selectNode(network.name);
      appStore.isSdkInitialized = true;
      checkWalletConnection();
      getFactory();
    } catch (e) {
      appStore.isSdkInitialized = false;
      console.error("SDK initialization failed", e);
    }
    if (network?.websocketUrl !== prevNetwork?.websocketUrl) {
      WebSocketClient.disconnect();
      WebSocketClient.connect(network.websocketUrl);
    }
  },
  { immediate: true }
);
</script>

<style lang="scss" scoped>
@use "@/styles/variables" as *;

.app {
  .app-main {
    width: 100%;
    margin-bottom: 56px;
    margin-top: 80px;
  }
}
</style>
